/**
 * 素材モーダルから Google 広告にアップロードする際、複数の連携アカウントから
 * アップロード先を選ばせる小型モーダル。
 *
 * MCC 配下で多数のクライアントがある場合、ドロップダウン内に全アカウントを
 * 列挙すると視認性が落ちるため、ここでまとめて選択させる。
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './GoogleAdsAccountPickerModal.module.css';

export interface GoogleAdsPickerAccount {
  id: string;
  account_id: string;
  account_display_name: string | null;
}

interface Props {
  isOpen: boolean;
  accounts: GoogleAdsPickerAccount[];
  onSelect: (accountId: string) => void;
  onClose: () => void;
  isUploading?: boolean;
}

export function GoogleAdsAccountPickerModal({
  isOpen,
  accounts,
  onSelect,
  onClose,
  isUploading = false,
}: Props) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Google 広告 — アップロード先を選択</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
        <p className={styles.description}>
          素材をアップロードする Google 広告アカウントを選んでください。
        </p>
        <ul className={styles.accountList}>
          {accounts.map((acc) => (
            <li key={acc.id}>
              <button
                type="button"
                className={styles.accountItem}
                onClick={() => onSelect(acc.account_id)}
                disabled={isUploading}
              >
                <span className={styles.accountName}>
                  {acc.account_display_name || '(名称未設定)'}
                </span>
                <span className={styles.accountId}>customer_id: {acc.account_id}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
